// Справка „По станции" (Справки → stations; доскоро „По обекти").
//
// Един ред = една станция за периода: колко сметки са я докоснали, колко е
// продала, какъв дял е това от заведението, колко е сторнирано и накъде мърда
// спрямо предходния равен отрязък. `GET /reports/analytics/stations` връща
// целия период наведнъж, затова таблицата работи в КЛИЕНТСКИ режим.
//
// Числата идват от ТРИ места и всяко има своя причина:
//   • обобщението по станции — оборот, сметки, разбивка по метод;
//   • разбивката по дни от същия отговор — за да се разреши id-то на
//     станцията, което обобщението не носи;
//   • `GET /bills/cancellations` — сторното, зад друго право.
//
// Филтърът „Сервитьори" е параметър на заявката (`user_ids` = сметките на
// тези сервитьори, както в „Продажби по периоди"); сторното се стеснява в
// браузъра по `waiter_id` — сервитьора НА СМЕТКАТА.
//
// Методите на плащане са КАТО в „По сервитьори": всички активни (+
// неактивните с пари в периода), в реда от настройките, със смесеното
// разпределено в „В брой" и „С карта" (виж `stations/stationsMath.ts`).

import { useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useStationsReport } from "../../../hooks/useReports";
import { useMenuStations } from "../../../hooks/useMenu";
import { useUsers } from "../../../hooks/useUsers";
import {
  usePaymentMethods,
  useActivePaymentMethods,
} from "../../../hooks/usePaymentMethods";
import {
  buildMethodEntries,
  type MethodEntry,
} from "../../../utils/paymentMethodsDisplay";
import type { CancelledItem } from "../../../api/reportsCancellations";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { previousPeriodOf } from "./reportPeriod";
import { pluralLabel } from "./reportPlural";
import { StationsKpis } from "./stations/StationsKpis";
import { StationsMethodSplit } from "./stations/StationsMethodSplit";
import {
  FilterChip,
  type FilterChipOption,
} from "./salesByPeriods/FilterChip";
import { useStationsColumns } from "./stations/stationsColumns";
import { exportStations } from "./stations/stationsExport";
import { useCanSeeVoids, useStationVoids } from "./stations/useStationVoids";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";
import {
  MIXED_PAYMENT_METHOD_ID,
  buildStationRows,
  sumRevenue,
  sumVoids,
  voidsByStation,
  type StationRow,
} from "./stations/stationsMath";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

/** Сторното само на избраните сервитьори; празен избор = всички. */
const onlyWaiters = (items: CancelledItem[], userIds: number[]) =>
  userIds.length
    ? items.filter(
        (item) => item.waiter_id != null && userIds.includes(item.waiter_id),
      )
    : items;

export const StationsReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const isBg = !i18n.language?.startsWith("en");
  const langCode = isBg ? "bg" : "en";

  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [visibleRows, setVisibleRows] = useState<StationRow[]>([]);
  const [waiterIds, setWaiterIds] = useState<string[]>([]);

  // Справката е без избор на час — периодът се клампва до цели дни.
  const filters = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return {};
    return {
      start_date: businessDayStartFrom(dateRange[0]).format("YYYY-MM-DD HH:mm:ss"),
      end_date: businessDayEndFrom(dateRange[1]).format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [dateRange]);
  // Сервитьорите отиват само в заявката за оборота. Сторното ползва голия
  // период — ключът му е общ със справка „Сторно" — и се стеснява в браузъра.
  const userIds = useMemo(() => waiterIds.map(Number), [waiterIds]);
  const reportFilters = useMemo(
    () => (userIds.length ? { ...filters, user_ids: userIds } : filters),
    [filters, userIds],
  );

  // Предходният равен по дължина отрязък — само за Δ-тата. Тече ли още
  // избраният период, краят се подрязва до изтеклото (виж `reportPeriod`).
  const previousFilters = useMemo(() => {
    if (!compare || !dateRange?.[0] || !dateRange?.[1]) return null;
    const previous = previousPeriodOf(
      businessDayStartFrom(dateRange[0]),
      businessDayEndFrom(dateRange[1]),
    );
    return {
      start_date: previous.start.format("YYYY-MM-DD HH:mm:ss"),
      end_date: previous.end.format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [compare, dateRange]);

  const previousReportFilters = useMemo(
    () =>
      previousFilters && userIds.length
        ? { ...previousFilters, user_ids: userIds }
        : previousFilters,
    [previousFilters, userIds],
  );

  const {
    data: report,
    isLoading,
    isFetching,
    refetch,
  } = useStationsReport(reportFilters);
  // При изключено сравнение се пита за СЪЩИЯ период — React Query го дава от
  // кеша, без втора мрежова заявка.
  // `isPlaceholderData` — hook-ът държи старите редове, докато новите се
  // зареждат; за Δ-тата това би значело „сравни периода със самия себе си"
  // за един кадър, затова placeholder-ът тук не се брои за отговор.
  const { data: previousReport, isPlaceholderData: previousIsPlaceholder } =
    useStationsReport(previousReportFilters ?? reportFilters);

  const canSeeVoids = useCanSeeVoids();
  // Без период заявката за сторно би поискала ЦЯЛАТА история — оборотът по
  // станции е агрегат и понася празен филтър, списъкът със сторнирани редове не.
  const hasRange = Boolean(dateRange?.[0] && dateRange?.[1]);
  const { data: allVoidItems } = useStationVoids(
    filters,
    canSeeVoids && hasRange,
  );
  const { data: allPreviousVoidItems } = useStationVoids(
    previousFilters ?? filters,
    canSeeVoids && hasRange && previousFilters !== null,
  );
  const voidItems = useMemo(
    () => onlyWaiters(allVoidItems ?? [], userIds),
    [allVoidItems, userIds],
  );
  const previousVoidItems = useMemo(
    () => onlyWaiters(allPreviousVoidItems ?? [], userIds),
    [allPreviousVoidItems, userIds],
  );

  const { data: users = [] } = useUsers();
  const waiterOptions = useMemo<FilterChipOption[]>(
    () =>
      users.map((user) => ({
        value: String(user.id),
        label: user.names || user.username,
      })),
    [users],
  );

  const summary = useMemo(() => report?.summary ?? [], [report]);
  const days = useMemo(() => report?.data ?? [], [report]);
  const previousSummary = useMemo(
    () =>
      previousFilters
        ? previousIsPlaceholder
          ? []
          : (previousReport?.summary ?? [])
        : null,
    [previousFilters, previousReport, previousIsPlaceholder],
  );

  // Имената в отговора идват от BE-то (винаги на български) и обобщението не
  // носи id — разбивката по дни е единственото място с двете заедно. Менюто
  // допълва станциите без оборот в периода (има ги само в сторното).
  const menuStations = useMenuStations();
  const { nameById, idByName } = useMemo(() => {
    const byId = new Map<number, string>();
    for (const day of days) byId.set(day.id, day.stationName);
    for (const station of menuStations) {
      if (byId.has(station.id)) continue;
      const translated =
        station.translations.find(
          (translation) => translation.languageCode === langCode,
        )?.name ?? station.translations[0]?.name;
      byId.set(station.id, translated ?? `#${station.id}`);
    }
    const byName = new Map<string, number>();
    for (const [id, name] of byId) if (!byName.has(name)) byName.set(name, id);
    return { nameById: byId, idByName: byName };
  }, [days, menuStations, langCode]);

  const { rows } = useMemo(
    () =>
      buildStationRows({
        summary,
        previousSummary,
        voidsById: canSeeVoids ? voidsByStation(voidItems) : new Map(),
        nameById,
        idByName,
      }),
    [summary, previousSummary, canSeeVoids, voidItems, nameById, idByName],
  );

  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      row.stationName.toLowerCase().includes(needle),
    );
  }, [rows, searchText]);

  // Кутийките и лентата по методи следват ТЪРСАЧКАТА, както таблицата, редът
  // „Общо" и Excel. Иначе един екран показва три различни обхвата: пишеш „Бар",
  // таблицата остава с един ред, а кутийката отгоре продължава да сумира целия
  // период — и нищо не обяснява разликата.
  const shownTotals = useMemo(
    () =>
      searchedRows.reduce(
        (acc, row) => ({
          revenue: acc.revenue + row.revenue,
          bills: acc.bills + row.bills,
          voidTotal: acc.voidTotal + row.voidTotal,
          voidRows: acc.voidRows + row.voidRows,
          stations: acc.stations + 1,
        }),
        { revenue: 0, bills: 0, voidTotal: 0, voidRows: 0, stations: 0 },
      ),
    [searchedRows],
  );

  const { data: activeMethods = [], isLoading: activeMethodsLoading } =
    useActivePaymentMethods();
  const { data: allMethods = [] } = usePaymentMethods();
  const methodEntries = useMemo<MethodEntry[]>(() => {
    const totalsByMethod = new Map<number, { amount: number; count: number }>();
    for (const row of searchedRows) {
      for (const [id, amount] of row.methodAmounts) {
        const entry = totalsByMethod.get(id) ?? { amount: 0, count: 0 };
        entry.amount += amount;
        entry.count += row.methodCounts.get(id) ?? 0;
        totalsByMethod.set(id, entry);
      }
    }
    // Смесеното няма своя колона: разпределено е в „В брой" и „С карта", а
    // активният метод „Смесено" иначе би дал празна колона.
    return buildMethodEntries({
      activeMethods: activeMethods.filter(
        (method) => method.id !== MIXED_PAYMENT_METHOD_ID,
      ),
      allMethods,
      totals: totalsByMethod,
      total: shownTotals.revenue,
      isBg,
    });
  }, [activeMethods, allMethods, searchedRows, shownTotals.revenue, isBg]);

  const previousRevenue = useMemo(
    () => (previousSummary ? sumRevenue(previousSummary) : null),
    [previousSummary],
  );
  const previousVoidTotal = useMemo(
    () =>
      canSeeVoids && previousFilters
        ? sumVoids(previousVoidItems).total
        : null,
    [canSeeVoids, previousFilters, previousVoidItems],
  );

  const columns = useStationsColumns({
    methodEntries,
    // Делът се смята спрямо ПОКАЗАНИЯ оборот — иначе при търсене процентите
    // сочат към знаменател, който не е на екрана.
    totalRevenue: shownTotals.revenue,
    showDelta: previousSummary !== null,
    showVoids: canSeeVoids,
  });

  const handleExport = () =>
    exportStations({
      rows: visibleRows,
      methodEntries,
      showVoids: canSeeVoids,
      showDelta: previousSummary !== null,
      t,
    });

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.stationsCountKpi")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          visibleRows.length,
          "reportsPage.stationsRowOne",
          "reportsPage.stationsRowMany",
        )}
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.stationsPageTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
          </div>

          {/* Таблицата чака и методите: колоните им трябва да са налице при
              първия рендер, иначе запомнената им видимост се губи. */}
          {isLoading || activeMethodsLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <StationsKpis
                totals={shownTotals}
                previousRevenue={previousRevenue}
                previousVoidTotal={previousVoidTotal}
                showVoids={canSeeVoids}
                isNarrow={narrow}
              />

              <StationsMethodSplit
                entries={methodEntries}
                total={shownTotals.revenue}
              />

              <DataTable<StationRow>
                tableId="stations"
                data={searchedRows}
                columns={columns}
                getRowId={(row) => row.stationName}
                refreshing={isFetching}
                ariaLabel={t("reportsPage.stationsPageTitle")}
                title={tableTitle}
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.stationsSearchPlaceholder"),
                }}
                onFilteredRowsChange={setVisibleRows}
                toolbarFilters={
                  <FilterChip
                    allLabel={t("reportsPage.stationsAllWaiters")}
                    label={t("reportsPage.stationsWaiters")}
                    options={waiterOptions}
                    selected={waiterIds}
                    onChange={setWaiterIds}
                  />
                }
                toolbarRight={
                  <ExportExcelButton
                    onExport={handleExport}
                    disabled={visibleRows.length === 0}
                    bare
                  />
                }
                initialSorting={[{ id: "revenue", desc: true }]}
                emptyTitle={t("reportsPage.stationsEmptyTitle")}
                emptyHint={t("reportsPage.stationsEmptyHint")}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
